import { useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import {
  useTaskTemplates,
  useCreateTaskTemplate,
  useUpdateTaskTemplate,
  useDeleteTaskTemplate,
} from '../../hooks/useTaskTemplates';
import { useDepartments } from '../../hooks/useDepartments';
import type { TaskTemplate, TaskTemplateItemInput } from '../../lib/taskTemplatesApi';
import type { TaskPriority } from '../../lib/tasksApi';
import { formatEstimate, parseEstimate } from '../../lib/estimate';
import { LabelPicker } from '../tasks/LabelPicker';
import { PRIORITY_OPTIONS } from '../tasks/taskConstants';
import { Select } from '../ui/Select';
import { useConfirm } from '../ui/ConfirmProvider';

// Settings → "Șabloane sarcini" (CLE-DASH).
//
// Two-pane layout mirroring LeadsPipelinesSettings: template list + create on
// the left, editor on the right. The editor keeps the whole template (metadata
// + item table) in local state and saves with a single PUT that replaces the
// item list wholesale. Templates can be applied when creating a project.

/**
 * Local editable copy of a template item. The estimate is kept as the
 * duration text the task form uses ("1h 30m") and parsed to hours on save.
 */
interface DraftItem {
  key: string;
  title: string;
  description: string;
  priority: TaskPriority;
  estimate: string;
  departmentId: string;
  labelIds: string[];
}

let draftSeq = 0;
function nextKey(): string {
  draftSeq += 1;
  return `draft-${draftSeq}`;
}

function toDraftItems(template: TaskTemplate): DraftItem[] {
  return [...template.items]
    .sort((a, b) => a.order - b.order)
    .map((it) => ({
      key: it.id,
      title: it.title,
      description: it.description ?? '',
      priority: it.priority,
      estimate: it.estimate != null ? formatEstimate(it.estimate, '') : '',
      departmentId: it.departmentId ?? '',
      labelIds: it.labels.map((l) => l.id),
    }));
}

function toItemInputs(items: DraftItem[]): TaskTemplateItemInput[] {
  return items.map((it) => ({
    title: it.title.trim(),
    description: it.description.trim() || null,
    priority: it.priority,
    estimate: parseEstimate(it.estimate).hours,
    departmentId: it.departmentId || null,
    labelIds: it.labelIds,
  }));
}

export function TaskTemplatesSettings() {
  const { data: templates, isLoading, isError, error } = useTaskTemplates();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // The editor reports its dirty state here so switching templates can warn
  // without lifting the whole draft into this component.
  const dirtyRef = useRef(false);
  const confirm = useConfirm();

  const selected = templates?.find((t) => t.id === selectedId) ?? templates?.[0] ?? null;

  async function handleSelect(id: string) {
    if (
      id !== selected?.id &&
      dirtyRef.current &&
      !(await confirm({ message: 'Aveți modificări nesalvate. Le abandonați?' }))
    )
      return;
    dirtyRef.current = false;
    setSelectedId(id);
  }

  return (
    <div className="max-w-5xl">
      <p className="text-sm text-gray-500">
        Configurați șabloane de sarcini reutilizabile. La crearea unui proiect puteți selecta un
        șablon, iar sarcinile lui sunt create automat în proiect.
      </p>

      {isLoading && <p className="mt-6 text-sm text-gray-500">Se încarcă șabloanele...</p>}
      {isError && (
        <p className="mt-6 text-sm text-red-600">
          Șabloanele nu au putut fi încărcate
          {error instanceof Error ? `: ${error.message}` : ''}.
        </p>
      )}

      {templates && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[260px_1fr]">
          <TemplateList
            templates={templates}
            selectedId={selected?.id ?? null}
            onSelect={handleSelect}
          />
          {selected ? (
            <TemplateEditor key={selected.id} template={selected} dirtyRef={dirtyRef} />
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">
              Niciun șablon. Creați unul pentru a începe.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Template list + create ----------------------------------------------------

function TemplateList({
  templates,
  selectedId,
  onSelect,
}: {
  templates: TaskTemplate[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const createTemplate = useCreateTaskTemplate();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handleCreate() {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);
    try {
      const created = await createTemplate.mutateAsync({ name: trimmed, items: [] });
      setName('');
      setCreating(false);
      onSelect(created.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Șablonul nu a putut fi creat.');
    }
  }

  return (
    <div>
      <div className="flex flex-col gap-1">
        {templates.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => onSelect(t.id)}
            className={`flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm ${
              t.id === selectedId
                ? 'bg-brand-50 font-medium text-brand-700'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span className="flex-1 truncate">{t.name}</span>
            <span className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
              {t.itemCount}
            </span>
          </button>
        ))}
      </div>

      {creating ? (
        <div className="mt-3 rounded-md border border-gray-200 p-3">
          <input
            autoFocus
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreate();
            }}
            placeholder="Nume șablon"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={handleCreate}
              disabled={!name.trim() || createTemplate.isPending}
              className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {createTemplate.isPending ? 'Se creează...' : 'Creează'}
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setError(null);
                setName('');
              }}
              className="inline-flex items-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Anulează
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="mt-3 inline-flex w-full items-center justify-center gap-1 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
        >
          <Plus size={15} /> Șablon nou
        </button>
      )}
    </div>
  );
}

// --- Template editor -------------------------------------------------------------

function TemplateEditor({
  template,
  dirtyRef,
}: {
  template: TaskTemplate;
  dirtyRef: MutableRefObject<boolean>;
}) {
  const updateTemplate = useUpdateTaskTemplate();
  const deleteTemplate = useDeleteTaskTemplate();
  const { data: departments } = useDepartments();
  const confirm = useConfirm();
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState(template.name);
  const [description, setDescription] = useState(template.description ?? '');
  const [departmentId, setDepartmentId] = useState(template.departmentId ?? '');
  const [items, setItems] = useState<DraftItem[]>(() => toDraftItems(template));
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);

  const departmentOptions = useMemo(
    () => (departments ?? []).map((d) => ({ value: d.id, label: d.name, color: d.color })),
    [departments],
  );

  const initial = useMemo(
    () =>
      JSON.stringify({
        name: template.name,
        description: template.description ?? '',
        departmentId: template.departmentId ?? '',
        items: toDraftItems(template).map(({ key: _key, ...rest }) => rest),
      }),
    [template],
  );
  const current = JSON.stringify({
    name,
    description,
    departmentId,
    items: items.map(({ key: _key, ...rest }) => rest),
  });
  const dirty = current !== initial;
  dirtyRef.current = dirty;

  const invalidItem = items.some((it) => !it.title.trim());
  // Same rule as the task form: a duration ("1h 30m", "30m", "2h"), at most
  // 999h — the backend's cap.
  const invalidEstimate = items.some((it) => {
    const parsed = parseEstimate(it.estimate);
    return parsed.error || (parsed.hours != null && parsed.hours > 999);
  });
  const canSave = dirty && name.trim().length > 0 && !invalidItem && !invalidEstimate;

  function patchItem(key: string, patch: Partial<DraftItem>) {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  }

  function handleDrop(targetKey: string) {
    const sourceKey = dragKey;
    setDragKey(null);
    setOverKey(null);
    if (!sourceKey || sourceKey === targetKey) return;
    setItems((prev) => {
      const next = [...prev];
      const from = next.findIndex((it) => it.key === sourceKey);
      const to = next.findIndex((it) => it.key === targetKey);
      if (from < 0 || to < 0) return prev;
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  }

  async function handleSave() {
    setError(null);
    try {
      await updateTemplate.mutateAsync({
        id: template.id,
        input: {
          name: name.trim(),
          description: description.trim() || null,
          departmentId: departmentId || null,
          items: toItemInputs(items),
        },
      });
      dirtyRef.current = false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Șablonul nu a putut fi salvat.');
    }
  }

  async function handleDelete() {
    setError(null);
    if (
      !(await confirm({
        message: `Ștergeți șablonul "${template.name}"? Această acțiune nu poate fi anulată.`,
        danger: true,
      }))
    )
      return;
    try {
      await deleteTemplate.mutateAsync(template.id);
      dirtyRef.current = false;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Șablonul nu a putut fi șters.');
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex flex-col gap-3">
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Nume șablon
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Descriere
          </span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Descriere opțională"
            className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
        <div className="max-w-xs">
          <Select
            label="Departament"
            value={departmentId}
            onChange={setDepartmentId}
            options={departmentOptions}
            placeholder="Fără departament"
            clearable
            emptyText="Niciun departament definit."
          />
          <p className="mt-1 text-xs text-gray-400">
            Departament implicit pentru sarcinile din șablon (opțional).
          </p>
        </div>
      </div>

      <div className="mt-5 border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">Sarcini</h3>
          <span className="text-xs text-gray-400">Trageți pentru a reordona</span>
        </div>

        <ul className="mt-3 flex flex-col gap-2">
          {items.length === 0 && (
            <li className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500">
              Nicio sarcină în șablon.
            </li>
          )}
          {items.map((item) => (
            <li
              key={item.key}
              draggable
              onDragStart={(e) => {
                setDragKey(item.key);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragEnd={() => {
                setDragKey(null);
                setOverKey(null);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setOverKey(item.key);
              }}
              onDrop={() => handleDrop(item.key)}
              className={`rounded-md border p-3 ${
                overKey === item.key && dragKey && dragKey !== item.key
                  ? 'border-brand-400 ring-1 ring-brand-300'
                  : 'border-gray-200'
              } ${dragKey === item.key ? 'opacity-50' : ''}`}
            >
              <div className="flex items-start gap-2">
                <GripVertical size={16} className="mt-2 shrink-0 cursor-grab text-gray-300" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      type="text"
                      value={item.title}
                      onChange={(e) => patchItem(item.key, { title: e.target.value })}
                      placeholder="Titlu sarcină"
                      className="min-w-[160px] flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                    />
                    <div className="w-32">
                      <Select
                        label="Prioritate"
                        hideLabel
                        value={item.priority}
                        onChange={(v) => patchItem(item.key, { priority: v as TaskPriority })}
                        options={PRIORITY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                        size="sm"
                      />
                    </div>
                    <input
                      type="text"
                      value={item.estimate}
                      onChange={(e) => patchItem(item.key, { estimate: e.target.value })}
                      placeholder="ex: 1h 30m"
                      aria-label="Estimare"
                      className={`w-28 rounded-md border px-2 py-1.5 text-sm ${
                        parseEstimate(item.estimate).error ? 'border-red-400' : 'border-gray-300'
                      }`}
                    />
                    <div className="w-40">
                      <Select
                        label="Departament sarcină"
                        hideLabel
                        value={item.departmentId}
                        onChange={(v) => patchItem(item.key, { departmentId: v })}
                        options={departmentOptions}
                        placeholder="Departament"
                        clearable
                        size="sm"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setItems((prev) => prev.filter((i) => i.key !== item.key))}
                      className="rounded-md p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      aria-label="Elimină sarcina"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                  {/* Same shape as the task form: a multi-line description and
                      the label pills, so a template item reads like the task
                      it will become. */}
                  <textarea
                    value={item.description}
                    onChange={(e) => patchItem(item.key, { description: e.target.value })}
                    rows={3}
                    placeholder="Descriere (opțional)"
                    className="mt-2 block w-full rounded-md border border-gray-200 px-3 py-1.5 text-sm"
                  />
                  <div className="mt-2">
                    <LabelPicker
                      size="sm"
                      value={item.labelIds}
                      onChange={(labelIds) => patchItem(item.key, { labelIds })}
                    />
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() =>
            setItems((prev) => [
              ...prev,
              {
                key: nextKey(),
                title: '',
                description: '',
                priority: 'NONE',
                estimate: '',
                departmentId: '',
                labelIds: [],
              },
            ])
          }
          className="mt-3 inline-flex items-center gap-1 rounded-md border border-dashed border-gray-300 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
        >
          <Plus size={15} /> Adaugă sarcină
        </button>
      </div>

      {error && (
        <p className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
          {error}
        </p>
      )}
      {invalidItem && (
        <p className="mt-2 text-xs text-amber-600">Toate sarcinile trebuie să aibă un titlu.</p>
      )}
      {invalidEstimate && (
        <p className="mt-2 text-xs text-amber-600">
          Estimările trebuie să fie durate valide (ex: 1h 30m, 30m, 2h), de cel mult 999h.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={!canSave || updateTemplate.isPending}
          className="inline-flex items-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {updateTemplate.isPending ? 'Se salvează...' : 'Salvează șablonul'}
        </button>
        <button
          type="button"
          onClick={handleDelete}
          disabled={deleteTemplate.isPending}
          className="ml-auto inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Trash2 size={14} /> Șterge șablon
        </button>
      </div>
    </div>
  );
}
